import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BlockerList, type Blocker } from './BlockerList';
import type { ModuleValidationState } from './ValidationProgress';

interface ValidationResultsProps {
  modules: ModuleValidationState[];
  organizationValid: boolean;
  organizationErrors: string[];
  organizationWarnings: string[];
  isNewOrganization: boolean;
}

export function ValidationResults({
  modules,
  organizationValid,
  organizationErrors,
  organizationWarnings,
  isNewOrganization,
}: ValidationResultsProps) {
  const hasErrors =
    (!isNewOrganization && !organizationValid) || modules.some((m) => m.status === 'invalid');
  const hasWarnings =
    (!isNewOrganization && organizationWarnings.length > 0) ||
    modules.some((m) => m.warnings.length > 0);

  // Collect all blockers (org blockers excluded when creating a new organization)
  const blockers: Blocker[] = [
    ...(!isNewOrganization
      ? [
          ...organizationErrors.map((msg) => ({
            message: msg,
            severity: 'error' as const,
            moduleKey: 'Organization',
          })),
          ...organizationWarnings.map((msg) => ({
            message: msg,
            severity: 'warning' as const,
            moduleKey: 'Organization',
          })),
        ]
      : []),
    ...modules.flatMap((m) => [
      ...m.errors.map((msg) => ({
        message: msg,
        severity: 'error' as const,
        moduleKey: m.moduleName,
      })),
      ...m.warnings.map((msg) => ({
        message: msg,
        severity: 'warning' as const,
        moduleKey: m.moduleName,
      })),
    ]),
  ];

  const errors = blockers.filter((b) => b.severity === 'error');
  const warnings = blockers.filter((b) => b.severity === 'warning');

  return (
    <div className="space-y-6">
      {/* Summary Alert */}
      {hasErrors ? (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Validation Failed</AlertTitle>
          <AlertDescription>
            There are {errors.length} {errors.length === 1 ? 'error' : 'errors'} that must be
            resolved before you can proceed. Please review the issues below.
          </AlertDescription>
        </Alert>
      ) : hasWarnings ? (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Validation Passed with Warnings</AlertTitle>
          <AlertDescription>
            Validation passed, but there are {warnings.length}{' '}
            {warnings.length === 1 ? 'warning' : 'warnings'} you should review before
            proceeding.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-success bg-success/10">
          <CheckCircle2 className="h-4 w-4 text-success" />
          <AlertTitle>Validation Successful</AlertTitle>
          <AlertDescription>
            All validations passed. You can proceed to the next step.
          </AlertDescription>
        </Alert>
      )}

      {/* Detailed Results */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Validation Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Organization Status — only shown for existing org lookups */}
          {!isNewOrganization && (
            <div className="flex items-center justify-between py-2 border-b">
              <span className="font-medium">Organization</span>
              <div className="flex items-center gap-2">
                {organizationValid ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-success" />
                    <span className="text-sm text-success">Valid</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-destructive" />
                    <span className="text-sm text-destructive">Invalid</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Module Statuses */}
          {modules.map((module) => (
            <div
              key={module.moduleKey}
              className="flex items-center justify-between py-2 border-b last:border-b-0"
            >
              <span className="font-medium">{module.moduleName}</span>
              <div className="flex items-center gap-2">
                {module.status === 'valid' && (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-success" />
                    <span className="text-sm text-success">Valid</span>
                  </>
                )}
                {module.status === 'invalid' && (
                  <>
                    <XCircle className="h-4 w-4 text-destructive" />
                    <span className="text-sm text-destructive">Invalid</span>
                  </>
                )}
                {module.warnings.length > 0 && module.status === 'valid' && (
                  <AlertTriangle className="h-4 w-4 text-warning" />
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Issues List */}
      {blockers.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Issues</CardTitle>
          </CardHeader>
          <CardContent>
            <BlockerList blockers={blockers} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
